/**
 * Journal Entries Page
 *
 * Lists all journal entries with filtering and navigation to create/edit.
 * This is the thin orchestrator — all state lives in useJournalEntries and all
 * heavy rendering is delegated to sub-components.
 */

import { useEffect, useCallback, useState, useRef } from 'react'


import { PageHeaderWithBreadcrumbs } from '@elsapiens/layout'
import { useShortcuts, useEscapeNavigation , usePageHeader } from '@elsapiens/providers'
import { Button, Table, Card } from '@elsapiens/ui'
import { cn } from '@elsapiens/utils'
import { Plus } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'


import { CurrencyViewToggle } from './CurrencyViewToggle'
import {
  JOURNAL_FEATURES,
  JOURNAL_STEPS,
  JOURNAL_WHAT_IS_IT,
} from './journal-entries-helpers'
import { buildEntryColumns, buildSkeletonColumns, buildSkeletonData } from './JournalEntriesColumns'
import { JournalEntriesFilterBar, JOURNAL_TABS } from './JournalEntriesFilterBar'
import { clearJournalListView, loadJournalListView, saveJournalListView } from './list-view-persistence'
import { useJournalEntries } from './useJournalEntries'
import { JournalEntryIllustration } from '../../components/illustrations/AccountingIllustrations'
import { MigrationBanner } from '../../components/MigrationBanner'
import { CollapsibleListInfo, EmptyState } from '../../components/ui'
import { PagePagination } from '../../components/ui/page-pagination'
import { useOrganization } from '../../stores/organization-store'
import { useSettings } from '../../stores/settings-store'

import type { JournalListView } from './list-view-persistence'

type JeState = ReturnType<typeof useJournalEntries>

function JeHeader({ activeTab, navigate, onSwitch }: {
  activeTab: string
  navigate: ReturnType<typeof useNavigate>
  onSwitch: (tabId: string) => void
}): JSX.Element {
  return (
    <PageHeaderWithBreadcrumbs
      description="View and manage accounting transactions"
      linkComponent={Link}
      title="Journal Entries"
      variant="card"
    >
      <DraftPostedToggle activeTab={activeTab} onSwitch={onSwitch} />
      <Button onClick={() => navigate('/journal/new')}>
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        New Entry
      </Button>
    </PageHeaderWithBreadcrumbs>
  )
}

function JeEmptyState({ navigate }: { navigate: ReturnType<typeof useNavigate> }): JSX.Element {
  return (
    <EmptyState
      description="Journal entries are the foundation of your accounting records. Create your first entry to start tracking financial transactions."
      features={JOURNAL_FEATURES}
      gettingStarted={JOURNAL_STEPS}
      illustration={<JournalEntryIllustration size="lg" />}
      primaryAction={{
        label: 'Create Entry',
        icon: <Plus className="w-4 h-4 mr-2" />,
        onClick: () => navigate('/journal/new'),
      }}
      title="No journal entries"
      whatIsIt={JOURNAL_WHAT_IS_IT}
    />
  )
}

function JeListBody({ je, navigate, settings, onOpenEntry }: {
  je: JeState
  navigate: ReturnType<typeof useNavigate>
  settings: ReturnType<typeof useSettings>['settings']
  onOpenEntry: (entryId: string) => void
}): JSX.Element {
  const entryColumns = buildEntryColumns(settings.baseCurrency, je.currencyView, settings.numberFormat)
  if (je.isLoading) {
    return (
      <>
        <Table inCard columns={buildSkeletonColumns()} data={buildSkeletonData()} hoverable={false} />
        <PagePagination shimmer currentPage={1} loadedCount={0} pageSize={1} totalPages={1} onPageChange={() => {}} />
      </>
    )
  }
  if (je.sortedEntries.length === 0) { return <JeEmptyState navigate={navigate} /> }
  return (
    <>
      <Table
        hoverable
        inCard
        columns={entryColumns}
        data={je.sortedEntries}
        onRowClick={(entry) => onOpenEntry(entry.id)}
      />
      <PagePagination
        currentPage={je.currentPage}
        isLoading={je.isLoadingMore}
        loadedCount={je.sortedEntries.length}
        pageSize={je.pageSize}
        totalCount={je.totalCount}
        totalPages={je.totalPages}
        onPageChange={(p) => { void je.goToPage(p) }}
      />
    </>
  )
}

/** The app's scrolling container, rendered by AppLayout. */
function getScrollEl(): HTMLElement | null {
  return document.querySelector('main')
}

// Wait two animation frames for the restored rows to lay out before scrolling.
function scrollAfterLayout(el: HTMLElement, top: number): void {
  requestAnimationFrame(() => requestAnimationFrame(() => { el.scrollTop = top }))
}

/**
 * Restores the saved scroll position (and any extra infinite-scroll pages) once,
 * after the first page has loaded. Sort/filters are already restored via the
 * hook's initial state, so the first fetch returns the correct order.
 */
function useRestoreListView(je: JeState, restoreView: JournalListView | null): void {
  const done = useRef(false)
  useEffect(() => {
    if (done.current) { return }
    if (!restoreView) { done.current = true; return }
    if (je.isLoading) { return }
    done.current = true
    void (async () => {
      if (restoreView.currentPage > 1) { await je.goToPage(restoreView.currentPage) }
      const el = getScrollEl()
      if (el) {
        scrollAfterLayout(el, restoreView.scrollTop)
      }
      clearJournalListView()
    })()
  }, [je, restoreView])
}

function useJeShortcuts(navigate: ReturnType<typeof useNavigate>, switchTab: (tabId: string) => void): void {
  const { register, pushContext, popContext } = useShortcuts()
  useEffect(() => {
    pushContext('journal-entries')
    return () => popContext('journal-entries')
  }, [pushContext, popContext])
  useEffect(() => {
    const cleanups = [
      register({ id: 'je-new', keys: 'n', label: 'New Entry', description: 'Create a new journal entry', category: 'Journal Entries', scope: 'context', context: 'journal-entries', action: () => navigate('/journal/new') }),
      register({ id: 'je-drafts', keys: 'k', label: 'View Drafts', description: 'Switch to draft entries', category: 'Journal Entries', scope: 'context', context: 'journal-entries', action: () => switchTab('drafts') }),
      register({ id: 'je-posted', keys: 'l', label: 'View Posted', description: 'Switch to posted entries', category: 'Journal Entries', scope: 'context', context: 'journal-entries', action: () => switchTab('posted') }),
    ]
    return () => cleanups.forEach(fn => fn())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register])
}

function useJePageChrome(): void {
  const navigate = useNavigate()
  const jeBreadcrumbs = [
    { label: 'Home', href: '/', as: Link },
    { label: 'Accounting', href: '/accounting', as: Link },
    { label: 'Journal Entries' },
  ]
  usePageHeader({
    title: 'Journal Entries',
    description: 'View and manage accounting transactions',
    breadcrumbs: jeBreadcrumbs,
  })
  useEscapeNavigation(navigate, jeBreadcrumbs)
}

function JeCurrencyToggle({ je, baseCurrency }: { je: JeState; baseCurrency: string }): JSX.Element {
  return (
    <div
      className="flex justify-end"
      style={{
        margin: '0 calc(var(--el-table-spacer-width) * -1)',
        padding: 'var(--el-layout-gap-sm) var(--el-table-spacer-width)',
      }}
    >
      <CurrencyViewToggle
        baseCurrency={baseCurrency}
        view={je.currencyView}
        onChange={je.setCurrencyView}
      />
    </div>
  )
}

function JeAboutInfo(): JSX.Element {
  return (
    <CollapsibleListInfo
      description="Journal entries record every financial transaction in your organization using double-entry bookkeeping."
      features={JOURNAL_FEATURES}
      gettingStarted={JOURNAL_STEPS}
      illustration={<JournalEntryIllustration size="lg" />}
      title="About Journal Entries"
      whatIsIt={JOURNAL_WHAT_IS_IT}
    />
  )
}

interface JeActions {
  openEntry: (entryId: string) => void
  switchTab: (tabId: string) => void
}

function useJeActions(opts: {
  je: JeState
  organizationId: string
  navigate: ReturnType<typeof useNavigate>
  setSearchParams: ReturnType<typeof useSearchParams>[1]
}): JeActions {
  const { je, organizationId, navigate, setSearchParams } = opts

  // Save the list view (sort, filters, scroll, loaded pages) when opening an
  // entry, so returning from its detail page lands back in the same place.
  const openEntry = useCallback((entryId: string) => {
    saveJournalListView({
      orgId: organizationId,
      sortField: je.sortField,
      sortDirection: je.sortDirection,
      searchQuery: je.searchQuery,
      conditions: je.conditions,
      activeTab: je.activeTab,
      scrollTop: getScrollEl()?.scrollTop ?? 0,
      currentPage: je.currentPage,
    })
    navigate(`/journal/${entryId}`)
  }, [organizationId, je, navigate])

  const switchTab = useCallback((tabId: string) => {
    const tab = JOURNAL_TABS.find(t => t.id === tabId)
    if (!tab) { return }
    je.setActiveTab(tab.id)
    je.setConditions(tab.conditions)
    if (tabId === 'all') { setSearchParams({}, { replace: true }) }
    else { setSearchParams({ tab: tabId }, { replace: true }) }
  }, [je, setSearchParams])

  return { openEntry, switchTab }
}

export function JournalEntries(): JSX.Element {
  const navigate = useNavigate()
  const { settings } = useSettings()
  const { organizationId } = useOrganization()
  const [searchParams, setSearchParams] = useSearchParams()
  useJePageChrome()

  const initialTab = searchParams.get('tab') ?? 'all'
  // Read the saved view once — it seeds sort/filters so the first fetch is
  // already in the right order; scroll/pages are restored by useRestoreListView.
  const [restoreView] = useState(() => loadJournalListView(organizationId))
  const je = useJournalEntries(initialTab, restoreView)
  useRestoreListView(je, restoreView)

  const { openEntry, switchTab } = useJeActions({ je, organizationId, navigate, setSearchParams })

  useJeShortcuts(navigate, switchTab)

  return (
    <div className="flex flex-col min-w-0">
      <JeHeader activeTab={je.activeTab} navigate={navigate} onSwitch={switchTab} />
      <div className="el-p-lg">
        <MigrationBanner />
        <Card className="overflow-hidden">
          <JournalEntriesFilterBar
            activeTab={je.activeTab}
            conditions={je.conditions}
            searchQuery={je.searchQuery}
            sortDirection={je.sortDirection}
            sortField={je.sortField}
            onActiveTabChange={switchTab}
            onConditionsChange={je.setConditions}
            onRefresh={() => { void je.fetchEntries() }}
            onSearchChange={je.setSearchQuery}
            onSortChange={(id, dir) => { je.setSortField(id); je.setSortDirection(dir) }}
          />
          <JeCurrencyToggle baseCurrency={settings.baseCurrency} je={je} />
          <JeListBody je={je} navigate={navigate} settings={settings} onOpenEntry={openEntry} />
        </Card>
        {!je.isLoading && je.sortedEntries.length > 0 && <JeAboutInfo />}
      </div>
    </div>
  )
}

// ── Shared Draft/Posted toggle ────────────────────────────────────────────────

export function DraftPostedToggle({
  activeTab,
  onSwitch,
}: {
  activeTab: string
  onSwitch: (tab: 'drafts' | 'posted') => void
}): JSX.Element {
  const draftsKey = 'K'
  const postedKey = 'L'

  return (
    <div className="flex items-center gap-2">
      <div className="flex rounded-lg border border-border overflow-hidden text-sm">
        <button
          className={cn(
            'px-3 py-1.5 font-medium transition-colors',
            activeTab === 'drafts'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
          title={`Show Drafts (${draftsKey})`}
          onClick={() => onSwitch('drafts')}
        >
          Drafts
        </button>
        <div className="w-px bg-border" />
        <button
          className={cn(
            'px-3 py-1.5 font-medium transition-colors',
            activeTab === 'posted'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
          title={`Show Posted (${postedKey})`}
          onClick={() => onSwitch('posted')}
        >
          Posted
        </button>
      </div>
      <span className="text-xs text-muted-foreground hidden sm:inline select-none">
        <kbd className="kbd">{draftsKey}</kbd> / <kbd className="kbd">{postedKey}</kbd>
      </span>
    </div>
  )
}
